import React, { useEffect, useState, memo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Tab } from "react-bootstrap";
import { Lock } from "react-iconly";

import lampOnImg from "./../../image/assets/lamp-on.svg";
import lampOffImg from "./../../image/assets/lamp-off.svg";
import { bookApi } from "../../utility/api/apiList";

import { showSimAlert } from "../../redux/actions/alert/alertActions";
import singleSimPageType from "../../redux/actions/singleSimPage/singleSimPageType";
import singleSimDetailType from "../../redux/actions/singleSimDetail/singleSimDetailType";

import useAxios from "../../hooks/useAxios";
import scrollToTop from "../../libs/scrollToTop";

import SimInfoTabContainer from "./simInfo";
import SimTabComponent from "../../components/public/singleSimTabs/sim";
import QuizTabContainer from "./quiz";
import ExerciseTabContainer from "./exercise";
import LabReportTabContainer from "./labReport";

import { SingleSimSection, StyledTabs } from "./styledSimContainer";

const SingleSimContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isIframe, setIsIframe] = useState(false);
  const isLogin = useSelector((state) => state.userDetail.isLogin);
  const [activeTabKey, setActiveTabKey] = useState("info");
  const simSinglePageDetail = useSelector((state) => state.singleSimPageDetail);
  const simSingleDetail = useSelector((state) => state.singleSimDetail);

  const isAccess =
    isLogin &&
    (simSinglePageDetail.singleSimDetail.is_free ||
      simSinglePageDetail.singleSimDetail.is_subscribed);

  let { id } = useParams();

  const { data: bookData, run: getBookDetail } = useAxios({
    url: `${bookApi}${id}/`,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.get("view-port") === "iframe" ? setIsIframe(true) : setIsIframe(false);

    dispatch({ type: singleSimPageType.START_FETCHING_SINGLE_SIM_PAGE, id });
    dispatch({
      type: singleSimDetailType.START_FETCHING_SINGLE_SIM_DETAILS,
      id,
    });
    scrollToTop();
    getBookDetail();
    document.title = simSinglePageDetail.singleSimDetail.name;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldPush = params.get("tab") ? params.get("tab") : "info";
    setActiveTabKey(shouldPush);
    if (activeTabKey === "sim") {
      dispatch(showSimAlert());
    }
  }, [activeTabKey]);

  const handleTabPush = (key) => {
    let searchUpdateAddress = `?tab=${key}`;
    const params = new URLSearchParams(window.location.search);
    if (params.get("view-port") === "iframe") {
      setIsIframe(true);
      searchUpdateAddress = searchUpdateAddress + "&view-port=iframe";
    } else {
      setIsIframe(false);
    }
    history.replace({ search: searchUpdateAddress });
    setActiveTabKey(key);
  };

  const TabIcon = memo(({ eventKey }) => {
    if (eventKey === activeTabKey) {
      return <img src={lampOnImg} alt="lamp-on" className="tab-icon" />;
    } else if (!isAccess) {
      return <Lock size="small" color="var(--text-color-muted)" set="bold" />;
    } else if (eventKey !== activeTabKey) {
      return <img src={lampOffImg} alt="lamp-off" className="tab-icon" />;
    }
  });

  const simTabsData = [
    {
      eventKey: "info",
      title: (
        <>
          <span>معرفی</span>
          {<TabIcon eventKey="info" />}
        </>
      ),
      childrenComponent: (
        <SimInfoTabContainer
          simID={id}
          simInfoData={simSinglePageDetail}
          bookData={bookData}
          isLogin={isLogin}
          isIframe={isIframe}
        />
      ),
    },
    {
      eventKey: "sim",
      title: (
        <>
          <span>آزمایش</span>
          {<TabIcon eventKey="sim" />}
        </>
      ),
      childrenComponent: (
        <SimTabComponent
          simSrc={simSingleDetail.simDetail.implement_path}
          data={simSinglePageDetail.singleSimDetail}
          error={simSingleDetail.errorMessage}
        />
      ),
    },
    {
      eventKey: "exercise",
      title: (
        <>
          <span>تمرین</span>
          {<TabIcon eventKey="exercise" />}
        </>
      ),
      childrenComponent: (
        <ExerciseTabContainer
          simID={id}
          simSingleDetail={simSingleDetail}
          loading={simSingleDetail.loading || simSingleDetail.exerciseLoading}
          error={simSingleDetail.error || simSingleDetail.exerciseError}
          errorMessage={
            simSingleDetail.errorMessage || simSingleDetail.exerciseErrorMessage
          }
          simSrc={simSingleDetail.simDetail.implement_path}
          simulationData={simSinglePageDetail.singleSimDetail}
        />
      ),
    },
    {
      eventKey: "quiz",
      title: (
        <>
          <span>آزمون</span>
          {<TabIcon eventKey="quiz" />}
        </>
      ),
      childrenComponent: (
        <QuizTabContainer
          simID={id}
          simSingleDetail={simSingleDetail}
          loading={simSingleDetail.loading || simSingleDetail.quizLoading}
          error={simSingleDetail.error || simSingleDetail.quizError}
          errorMessage={
            simSingleDetail.errorMessage || simSingleDetail.quizErrorMessage
          }
        />
      ),
    },
    {
      eventKey: "lab-report",
      title: (
        <>
          <span>گزارش‌کار</span>
          {<TabIcon eventKey="lab-report" />}
        </>
      ),
      childrenComponent: <LabReportTabContainer />,
    },
  ];

  return (
    <SingleSimSection className="p-3 p-md-5">
      <StyledTabs
        defaultActiveKey="info"
        id="sim-tabs"
        activeKey={activeTabKey}
        onSelect={(k) => handleTabPush(k)}
      >
        {simTabsData.map((tab, index) => {
          return (
            <Tab
              key={index}
              eventKey={tab.eventKey}
              title={tab.title}
              disabled={!isAccess}
              tabClassName="sim-tab text-white"
            >
              {tab.childrenComponent}
            </Tab>
          );
        })}
      </StyledTabs>
    </SingleSimSection>
  );
};

export default SingleSimContainer;
